import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

const PITCH_DECK_URL = ''; // Set this to your PDF URL when ready, e.g. 'https://your-link.com/faasley-pitch-deck.pdf'
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

const stats = [
  { value: '9+', label: 'Product Brands', icon: 'ri-store-2-line' },
  { value: '₹8–9L', label: 'Investment Ask', icon: 'ri-money-rupee-circle-line' },
  { value: '10–12%', label: 'Equity Offered', icon: 'ri-pie-chart-2-line' },
  { value: '5+', label: 'Product Categories', icon: 'ri-layout-grid-line' },
];

const useOfFunds = [
  { label: 'Raw Material & Production', percent: 40, amount: '₹3.2–3.6L' },
  { label: 'Packaging & Branding', percent: 25, amount: '₹2–2.25L' },
  { label: 'Marketing & Launch', percent: 20, amount: '₹1.6–1.8L' },
  { label: 'Operations & Logistics', percent: 15, amount: '₹1.2–1.35L' },
];

const brands = [
  { name: 'Fassnut', category: 'Makhana Snacks', icon: 'ri-seedling-line', desc: 'Premium roasted makhana in multiple flavours' },
  { name: 'Fassfalli', category: 'Dry Fruits & Nuts', icon: 'ri-leaf-line', desc: 'Curated mix of quality dry fruits' },
  { name: 'Rajsip', category: 'Spices', icon: 'ri-fire-line', desc: 'Authentic Indian spices — Jeera, Haldi, Dhaniya' },
  { name: 'Faastea / Booston', category: 'Tea', icon: 'ri-cup-line', desc: 'Premium chai blends for every mood' },
  { name: 'Sachar', category: 'Pickles', icon: 'ri-restaurant-line', desc: 'Homemade-style authentic Indian pickles' },
  { name: 'Watler', category: 'Drinking Water', icon: 'ri-drop-line', desc: 'Pure packaged drinking water' },
];

const whyInvest = [
  { icon: 'ri-line-chart-line', title: 'Booming FMCG Market', desc: 'India\'s packaged food market is growing at 12–15% CAGR, driven by health-conscious urban consumers.' },
  { icon: 'ri-shield-check-line', title: 'FSSAI Compliant', desc: 'All products follow strict food safety standards ensuring consumer trust and regulatory compliance.' },
  { icon: 'ri-heart-line', title: 'Authentic & Homemade', desc: 'Products crafted with traditional recipes, targeting the growing demand for clean-label foods.' },
  { icon: 'ri-global-line', title: 'NRI Export Potential', desc: 'Strong demand for authentic Indian food products in international markets and NRI communities.' },
  { icon: 'ri-user-star-line', title: 'Experienced Founder', desc: 'Kamini Thakur brings hands-on expertise in product development, manufacturing, and quality control.' },
  { icon: 'ri-bar-chart-grouped-line', title: 'Multi-Brand Strategy', desc: '9+ brands across 5 categories reduces risk and maximises market reach from day one.' },
];

const timeline = [
  { year: '2024', title: 'Company Formation', desc: 'Faasleyfoods Pvt. Ltd. incorporated. Brand identity and product line finalised.' },
  { year: 'Q1 2025', title: 'Seed Investment', desc: 'Strategic partner onboarded. Production setup and packaging design completed.' },
  { year: 'Q2 2025', title: 'Market Launch', desc: 'Regional launch across local retail, online marketplaces, and direct sales.' },
  { year: '2026', title: 'National Expansion', desc: 'Pan-India distribution through modern trade, e-commerce, and B2B channels.' },
  { year: '2027+', title: 'Export & Scale', desc: 'International markets — UAE, UK, USA — targeting NRI communities.' },
];

const tamStats = [
  { value: '₹4.5L Cr', label: 'India Packaged Food Market', sub: 'Total Addressable Market (2024)', icon: 'ri-global-line' },
  { value: '15%', label: 'Annual CAGR', sub: 'Projected growth through 2030', icon: 'ri-line-chart-line' },
  { value: '₹28,000 Cr', label: 'Makhana & Snacks Segment', sub: 'Growing at 18% CAGR', icon: 'ri-seedling-line' },
  { value: '500M+', label: 'Target Consumers', sub: 'Urban & semi-urban India', icon: 'ri-user-3-line' },
];

const categorySegments = [
  { label: 'Packaged Snacks & Makhana', size: '₹28,000 Cr', growth: '+18% CAGR', barWidth: 90, relevance: 'Core' },
  { label: 'Indian Spices & Masala', size: '₹22,000 Cr', growth: '+12% CAGR', barWidth: 75, relevance: 'Core' },
  { label: 'Packaged Tea', size: '₹18,500 Cr', growth: '+10% CAGR', barWidth: 62, relevance: 'Core' },
  { label: 'Pickles & Condiments', size: '₹8,200 Cr', growth: '+14% CAGR', barWidth: 42, relevance: 'Core' },
  { label: 'Packaged Drinking Water', size: '₹24,000 Cr', growth: '+16% CAGR', barWidth: 80, relevance: 'Core' },
];

const growthYears = [
  { year: '2020', value: 28, label: '₹2.8L Cr' },
  { year: '2021', value: 34, label: '₹3.4L Cr' },
  { year: '2022', value: 38, label: '₹3.8L Cr' },
  { year: '2023', value: 42, label: '₹4.2L Cr' },
  { year: '2024', value: 48, label: '₹4.5L Cr' },
  { year: '2025E', value: 55, label: '₹5.2L Cr' },
  { year: '2026E', value: 63, label: '₹6.0L Cr' },
  { year: '2027E', value: 73, label: '₹6.9L Cr' },
];

const tailwinds = [
  { icon: 'ri-smartphone-line', title: 'Digital Commerce Boom', desc: 'Online grocery grew 3x post-pandemic. D2C food brands now reach Tier 2 & 3 cities directly.' },
  { icon: 'ri-heart-pulse-line', title: 'Health-Conscious Shift', desc: 'Urban Indians actively replacing junk food with clean-label, natural snacks like Makhana.' },
  { icon: 'ri-home-heart-line', title: 'Homemade Nostalgia', desc: 'Post-COVID demand for authentic, homemade-style products surged 40% across all categories.' },
  { icon: 'ri-flight-takeoff-line', title: 'NRI Export Demand', desc: '35M+ NRIs globally seek authentic Indian food. Export market valued at ₹45,000 Cr and growing.' },
];

function useCountUp(target: number, duration = 1800, start = false) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime: number | null = null;
    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      setCount(Math.floor(progress * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [target, duration, start]);
  return count;
}

const SectionHeader = ({ icon, title, subtitle }: { icon: string; title: string; subtitle?: string }) => (
  <div className="text-center mb-12">
    <div className="flex items-center justify-center gap-4 mb-4">
      <div className="h-px flex-1 max-w-[80px]" style={{ background: '#c9a96e' }}></div>
      <i className={`${icon} text-base`} style={{ color: '#c9a96e' }}></i>
      <h2
        className="font-bold px-2"
        style={{ fontFamily: "'Playfair Display', serif", fontSize: '1.75rem', color: '#2c1a0e' }}
      >
        {title}
      </h2>
      <i className={`${icon} text-base`} style={{ color: '#c9a96e' }}></i>
      <div className="h-px flex-1 max-w-[80px]" style={{ background: '#c9a96e' }}></div>
    </div>
    {subtitle && (
      <p className="text-sm max-w-xl mx-auto" style={{ color: '#7a4f2a' }}>{subtitle}</p>
    )}
    <div className="flex items-center justify-center gap-2 mt-4">
      <div className="h-px w-20" style={{ background: '#e8d5b7' }}></div>
      <div className="w-1.5 h-1.5 rounded-full" style={{ background: '#c9a96e' }}></div>
      <div className="h-px w-20" style={{ background: '#e8d5b7' }}></div>
    </div>
  </div>
);

function PitchDeckModal({ onClose }: { onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(44,26,14,0.6)', backdropFilter: 'blur(4px)' }}
      onClick={onClose}
    >
      <div
        className="relative rounded-2xl p-8 md:p-10 max-w-md w-full text-center"
        style={{ background: '#faf7f2', border: '1px solid #e8d5b7' }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full cursor-pointer transition-all"
          style={{ background: '#f5ede0', color: '#7a4f2a' }}
        >
          <i className="ri-close-line text-sm"></i>
        </button>

        <div
          className="w-16 h-16 flex items-center justify-center rounded-2xl mx-auto mb-5"
          style={{ background: '#f5ede0', border: '1px solid #e8d5b7' }}
        >
          <i className="ri-file-pdf-2-line text-3xl" style={{ color: '#c9a96e' }}></i>
        </div>

        <h3
          className="font-bold text-2xl mb-2"
          style={{ fontFamily: "'Playfair Display', serif", color: '#2c1a0e' }}
        >
          Pitch Deck Coming Soon
        </h3>
        <p className="text-sm leading-relaxed mb-7" style={{ color: '#7a4f2a' }}>
          Our full investor pitch deck is being finalised. Request a copy directly and we'll send it to you within 24 hours.
        </p>

        <div className="space-y-3">
          <a
            href="https://wa.me/918800889959?text=Hi%20Kamini%2C%20I%20would%20like%20to%20request%20the%20Faasley%20Foods%20investor%20pitch%20deck.%20Please%20share%20it%20with%20me."
            target="_blank"
            rel="nofollow noreferrer"
            className="flex items-center justify-center gap-2 w-full font-bold py-3.5 rounded-md text-sm transition-all whitespace-nowrap cursor-pointer"
            style={{ background: '#25D366', color: '#fff' }}
          >
            <i className="ri-whatsapp-line"></i>
            Request via WhatsApp
          </a>
          <a
            href="mailto:faasley@gmail.com?subject=Pitch%20Deck%20Request%20-%20Faasley%20Foods&body=Hi%20Kamini%2C%0A%0AI%20am%20interested%20in%20investing%20in%20Faasley%20Foods%20and%20would%20like%20to%20receive%20the%20investor%20pitch%20deck.%0A%0APlease%20share%20it%20at%20your%20earliest%20convenience.%0A%0AThank%20you."
            className="flex items-center justify-center gap-2 w-full font-semibold py-3.5 rounded-md text-sm transition-all whitespace-nowrap cursor-pointer"
            style={{ background: '#f5ede0', color: '#3b2010', border: '1px solid #e8d5b7' }}
          >
            <i className="ri-mail-line"></i>
            Request via Email
          </a>
        </div>

        <p className="text-[10px] mt-5" style={{ color: '#7a4f2a' }}>
          We respond within 24 hours. All information is kept strictly confidential.
        </p>
      </div>
    </div>
  );
}

function DownloadPitchDeckButton({ variant = 'dark' }: { variant?: 'dark' | 'outline' }) {
  const [showModal, setShowModal] = useState(false);

  const handleClick = () => {
    if (PITCH_DECK_URL) {
      window.open(PITCH_DECK_URL, '_blank', 'noopener,noreferrer');
    } else {
      setShowModal(true);
    }
  };

  return (
    <>
      <button
        onClick={handleClick}
        className="inline-flex items-center justify-center gap-2 font-semibold px-8 py-3.5 rounded-md text-sm transition-all whitespace-nowrap cursor-pointer"
        style={
          variant === 'dark'
            ? { background: '#c9a96e', color: '#2c1a0e' }
            : { border: '1px solid #c9a96e', color: '#3b2010', background: 'transparent' }
        }
      >
        <i className="ri-file-download-line"></i>
        Download Pitch Deck
      </button>
      {showModal && <PitchDeckModal onClose={() => setShowModal(false)} />}
    </>
  );
}

const founderCredentials = [
  { icon: 'ri-building-line', label: 'Company', value: 'Faasleyfoods Pvt. Ltd.', sub: 'Incorporated 2024' },
  { icon: 'ri-shield-check-line', label: 'Compliance', value: 'FSSAI Certified', sub: 'All products food-safe' },
  { icon: 'ri-store-2-line', label: 'Portfolio', value: '9+ Products', sub: 'Across 5 categories' },
  { icon: 'ri-map-pin-line', label: 'Base', value: 'India', sub: 'Pan-India vision' },
];

const founderSkills = [
  { icon: 'ri-flask-line', title: 'Product Development', desc: 'Hands-on formulation of recipes, taste testing, and product iteration across all 9 SKUs from scratch.' },
  { icon: 'ri-settings-3-line', title: 'Manufacturing Setup', desc: 'End-to-end production planning — sourcing raw materials, setting up hygienic processing units, and quality checks.' },
  { icon: 'ri-award-line', title: 'Quality Control', desc: 'Strict adherence to FSSAI standards. Every batch is tested before packaging to ensure consumer safety.' },
  { icon: 'ri-palette-line', title: 'Brand Building', desc: 'Conceptualised and designed all 6 brand identities — Fassnut, Fassfalli, Rajsip, Faastea, Sachar, and Watler.' },
  { icon: 'ri-truck-line', title: 'Supply Chain', desc: 'Built vendor relationships for raw materials, packaging, and logistics from the ground up.' },
  { icon: 'ri-heart-line', title: 'Consumer Insight', desc: 'Deep understanding of Indian consumer preferences — especially the demand for clean-label, homemade-style foods.' },
];

function FounderSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="py-20 md:py-28"
      style={{ background: 'linear-gradient(to bottom, #f5ede0, #faf7f2)', borderTop: '1px solid #e8d5b7' }}
    >
      <div className="w-full px-4 sm:px-6 lg:px-12 xl:px-20">
        <SectionHeader
          icon="ri-user-star-line"
          title="Meet the Founder"
          subtitle="The vision, drive, and expertise behind Faasley Foods"
        />

        {/* ── Top: Photo + Bio ── */}
        <div className="grid lg:grid-cols-5 gap-10 mb-16 items-start">

          {/* Left: Photo column */}
          <div className="lg:col-span-2 flex flex-col gap-5">
            <div
              className="relative rounded-2xl overflow-hidden"
              style={{
                border: '1px solid #e8d5b7',
                transform: visible ? 'translateY(0)' : 'translateY(24px)',
                opacity: visible ? 1 : 0,
                transition: 'all 0.6s ease',
              }}
            >
              <div style={{ height: '420px' }}>
                <img
                  src="https://storage.readdy-site.link/project_files/c44a33b9-73a7-4f29-8c86-80a04dae53c1/5cb346ab-a7f1-4cf6-953f-cf38d742223f_Kamini.jpg?v=7ff1f630b615292716d05c68d8042a2b"
                  alt="Kamini Thakur — Founder & CEO, Faasleyfoods"
                  className="w-full h-full object-cover object-top"
                />
                <div
                  className="absolute inset-0"
                  style={{ background: 'linear-gradient(to top, rgba(44,26,14,0.75) 0%, transparent 50%)' }}
                />
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <p
                  className="font-bold text-2xl mb-0.5"
                  style={{ fontFamily: "'Playfair Display', serif", color: '#faf7f2' }}
                >
                  Kamini Thakur
                </p>
                <p className="text-sm font-medium mb-3" style={{ color: '#c9a96e' }}>Founder &amp; CEO</p>
                <div className="flex flex-wrap gap-2">
                  {['FSSAI Certified', 'Product Expert', 'Brand Builder'].map((tag) => (
                    <span
                      key={tag}
                      className="text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wider"
                      style={{ background: 'rgba(201,169,110,0.25)', color: '#c9a96e', border: '1px solid rgba(201,169,110,0.4)' }}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Credential tiles */}
            <div className="grid grid-cols-2 gap-3">
              {founderCredentials.map((cred, i) => (
                <div
                  key={cred.label}
                  className="rounded-xl p-4 text-center"
                  style={{
                    background: i % 2 === 0 ? '#3b2010' : '#faf7f2',
                    border: '1px solid #e8d5b7',
                    transform: visible ? 'translateY(0)' : 'translateY(16px)',
                    opacity: visible ? 1 : 0,
                    transition: 'all 0.5s ease',
                    transitionDelay: `${200 + i * 80}ms`,
                  }}
                >
                  <div
                    className="w-8 h-8 flex items-center justify-center rounded-lg mx-auto mb-2"
                    style={{ background: i % 2 === 0 ? 'rgba(201,169,110,0.2)' : '#f5ede0' }}
                  >
                    <i className={`${cred.icon} text-sm`} style={{ color: '#c9a96e' }}></i>
                  </div>
                  <p
                    className="font-bold text-sm leading-tight mb-0.5"
                    style={{ color: i % 2 === 0 ? '#faf7f2' : '#2c1a0e' }}
                  >
                    {cred.value}
                  </p>
                  <p
                    className="text-[10px]"
                    style={{ color: i % 2 === 0 ? 'rgba(232,213,183,0.7)' : '#7a4f2a' }}
                  >
                    {cred.sub}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Bio + Vision */}
          <div
            className="lg:col-span-3"
            style={{
              transform: visible ? 'translateY(0)' : 'translateY(24px)',
              opacity: visible ? 1 : 0,
              transition: 'all 0.6s ease 0.15s',
            }}
          >
            {/* Name + role header */}
            <div className="mb-7">
              <p className="text-xs font-bold uppercase tracking-widest mb-2" style={{ color: '#c9a96e' }}>
                Founder Profile
              </p>
              <h3
                className="font-bold mb-1"
                style={{ fontFamily: "'Playfair Display', serif", fontSize: '2rem', color: '#2c1a0e' }}
              >
                Kamini Thakur
              </h3>
              <p className="text-base font-medium" style={{ color: '#7a4f2a' }}>
                Founder &amp; CEO — Faasleyfoods Pvt. Ltd.
              </p>
            </div>

            {/* Bio paragraphs */}
            <div className="space-y-4 mb-8">
              <p className="text-sm leading-relaxed" style={{ color: '#5c3d1e' }}>
                Kamini Thakur is the founder and driving force behind Faasley Foods — a premium Indian packaged food brand built on the belief that authentic, homemade-style food deserves a place on every Indian shelf. With a deep passion for food, quality, and entrepreneurship, Kamini has single-handedly conceptualised, developed, and launched a portfolio of 9+ products across 5 distinct categories.
              </p>
              <p className="text-sm leading-relaxed" style={{ color: '#5c3d1e' }}>
                From sourcing the finest raw materials to designing brand identities and ensuring FSSAI compliance, Kamini has been hands-on at every stage of building Faasley. Her expertise spans product development, manufacturing setup, quality control, and creative branding — a rare combination that gives Faasley a strong operational foundation from day one.
              </p>
              <p className="text-sm leading-relaxed" style={{ color: '#5c3d1e' }}>
                Kamini's vision is clear: to build India's next great food brand — one that consumers trust for its purity, love for its taste, and choose for its values. She is now seeking a strategic partner who can bring marketing muscle, distribution reach, and business scaling expertise to accelerate Faasley's growth across India and beyond.
              </p>
            </div>

            {/* Vision quote */}
            <div
              className="rounded-2xl p-7 mb-8"
              style={{ background: '#3b2010', border: '1px solid rgba(201,169,110,0.3)' }}
            >
              <i className="ri-double-quotes-l text-3xl mb-4 block" style={{ color: 'rgba(201,169,110,0.4)' }}></i>
              <blockquote
                className="text-base italic leading-relaxed mb-4"
                style={{ fontFamily: "'Playfair Display', serif", color: '#faf7f2' }}
              >
                "Faasley एक premium food brand होगा जो hygienic, homemade style और authentic taste वाले products बनाएगा। हमारा goal है कि हर Indian घर में Faasley का एक product हो।"
              </blockquote>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0" style={{ border: '2px solid #c9a96e' }}>
                  <img
                    src="https://storage.readdy-site.link/project_files/c44a33b9-73a7-4f29-8c86-80a04dae53c1/5cb346ab-a7f1-4cf6-953f-cf38d742223f_Kamini.jpg?v=7ff1f630b615292716d05c68d8042a2b"
                    alt="Kamini Thakur"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div>
                  <p className="text-xs font-bold" style={{ color: '#c9a96e' }}>Kamini Thakur</p>
                  <p className="text-[10px]" style={{ color: 'rgba(232,213,183,0.7)' }}>Founder &amp; CEO, Faasley Foods</p>
                </div>
              </div>
            </div>

            {/* Contact row */}
            <div className="flex flex-wrap gap-3">
              <a
                href="https://wa.me/918800889959?text=Hi%20Kamini%2C%20I%20would%20like%20to%20connect%20regarding%20Faasley%20Foods."
                target="_blank"
                rel="nofollow noreferrer"
                className="inline-flex items-center gap-2 font-semibold px-5 py-3 rounded-md text-sm transition-all whitespace-nowrap cursor-pointer"
                style={{ background: '#25D366', color: '#fff' }}
              >
                <i className="ri-whatsapp-line"></i>
                +91 8800889959
              </a>
              <a
                href="mailto:faasley@gmail.com"
                className="inline-flex items-center gap-2 font-semibold px-5 py-3 rounded-md text-sm transition-all whitespace-nowrap cursor-pointer"
                style={{ background: '#faf7f2', color: '#3b2010', border: '1px solid #e8d5b7' }}
              >
                <i className="ri-mail-line"></i>
                faasley@gmail.com
              </a>
            </div>
          </div>
        </div>

        {/* ── Bottom: Skills Grid ── */}
        <div>
          <div className="flex items-center gap-4 mb-8">
            <div className="h-px flex-1" style={{ background: '#e8d5b7' }}></div>
            <p className="text-xs font-bold uppercase tracking-widest px-3" style={{ color: '#c9a96e' }}>
              Core Competencies
            </p>
            <div className="h-px flex-1" style={{ background: '#e8d5b7' }}></div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {founderSkills.map((skill, i) => (
              <div
                key={skill.title}
                className="rounded-xl p-6 flex gap-4"
                style={{
                  background: '#faf7f2',
                  border: '1px solid #e8d5b7',
                  transform: visible ? 'translateY(0)' : 'translateY(20px)',
                  opacity: visible ? 1 : 0,
                  transition: 'all 0.5s ease',
                  transitionDelay: `${300 + i * 80}ms`,
                }}
              >
                <div
                  className="w-11 h-11 flex items-center justify-center rounded-xl flex-shrink-0"
                  style={{ background: '#f5ede0', border: '1px solid #e8d5b7' }}
                >
                  <i className={`${skill.icon} text-lg`} style={{ color: '#c9a96e' }}></i>
                </div>
                <div>
                  <h4
                    className="font-bold text-sm mb-1.5"
                    style={{ fontFamily: "'Playfair Display', serif", color: '#2c1a0e' }}
                  >
                    {skill.title}
                  </h4>
                  <p className="text-xs leading-relaxed" style={{ color: '#7a4f2a' }}>{skill.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

      </div>
    </section>
  );
}

function MarketSizeSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [barsVisible, setBarsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          setTimeout(() => setBarsVisible(true), 300);
        }
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="py-20 md:py-28"
      style={{ background: 'linear-gradient(to bottom, #faf7f2, #f5ede0)' }}
    >
      <div className="w-full px-4 sm:px-6 lg:px-12 xl:px-20">
        <SectionHeader
          icon="ri-bar-chart-2-line"
          title="Market Opportunity"
          subtitle="India's packaged food industry is one of the fastest-growing consumer sectors in the world"
        />

        {/* ── Top 4 TAM Stats ── */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-16">
          {tamStats.map((stat, i) => (
            <div
              key={stat.label}
              className="rounded-2xl p-6 text-center transition-all duration-500"
              style={{
                background: i % 2 === 0 ? '#3b2010' : '#faf7f2',
                border: '1px solid #e8d5b7',
                transform: visible ? 'translateY(0)' : 'translateY(24px)',
                opacity: visible ? 1 : 0,
                transitionDelay: `${i * 100}ms`,
              }}
            >
              <div
                className="w-11 h-11 flex items-center justify-center rounded-xl mx-auto mb-4"
                style={{ background: i % 2 === 0 ? 'rgba(201,169,110,0.2)' : '#f5ede0' }}
              >
                <i className={`${stat.icon} text-lg`} style={{ color: '#c9a96e' }}></i>
              </div>
              <p
                className="font-bold text-2xl md:text-3xl mb-1 leading-none"
                style={{
                  fontFamily: "'Playfair Display', serif",
                  color: i % 2 === 0 ? '#c9a96e' : '#2c1a0e',
                }}
              >
                {stat.value}
              </p>
              <p
                className="font-semibold text-xs mb-1"
                style={{ color: i % 2 === 0 ? '#e8d5b7' : '#2c1a0e' }}
              >
                {stat.label}
              </p>
              <p
                className="text-[10px] leading-snug"
                style={{ color: i % 2 === 0 ? 'rgba(232,213,183,0.7)' : '#7a4f2a' }}
              >
                {stat.sub}
              </p>
            </div>
          ))}
        </div>

        {/* ── Two-column: Chart + Category Breakdown ── */}
        <div className="grid lg:grid-cols-2 gap-10 mb-16">

          {/* Left: Market Growth Bar Chart */}
          <div
            className="rounded-2xl p-7 md:p-8"
            style={{ background: '#faf7f2', border: '1px solid #e8d5b7' }}
          >
            <div className="flex items-start justify-between mb-6">
              <div>
                <p className="text-xs font-bold uppercase tracking-widest mb-1" style={{ color: '#c9a96e' }}>Market Growth</p>
                <h3
                  className="font-bold text-xl"
                  style={{ fontFamily: "'Playfair Display', serif", color: '#2c1a0e' }}
                >
                  India Packaged Food Market
                </h3>
                <p className="text-xs mt-1" style={{ color: '#7a4f2a' }}>Total market size in ₹ Lakh Crore (2020–2027E)</p>
              </div>
              <span
                className="text-xs font-bold px-3 py-1.5 rounded-full whitespace-nowrap"
                style={{ background: '#f5ede0', color: '#c9a96e', border: '1px solid #e8d5b7' }}
              >
                +15% CAGR
              </span>
            </div>

            {/* Bar chart */}
            <div className="flex items-end gap-2 h-44">
              {growthYears.map((yr, i) => {
                const isProjected = yr.year.includes('E');
                const heightPct = (yr.value / 73) * 100;
                return (
                  <div key={yr.year} className="flex-1 flex flex-col items-center gap-1.5">
                    <span
                      className="text-[9px] font-bold"
                      style={{ color: '#c9a96e', opacity: barsVisible ? 1 : 0, transition: `opacity 0.4s ${i * 80}ms` }}
                    >
                      {yr.label}
                    </span>
                    <div className="w-full flex items-end" style={{ height: '120px' }}>
                      <div
                        className="w-full rounded-t-lg transition-all duration-700"
                        style={{
                          height: barsVisible ? `${heightPct}%` : '0%',
                          transitionDelay: `${i * 80}ms`,
                          background: isProjected
                            ? 'repeating-linear-gradient(45deg, #c9a96e, #c9a96e 3px, #e8d5b7 3px, #e8d5b7 7px)'
                            : '#3b2010',
                          opacity: isProjected ? 0.75 : 1,
                        }}
                      />
                    </div>
                    <span className="text-[9px]" style={{ color: '#7a4f2a' }}>{yr.year}</span>
                  </div>
                );
              })}
            </div>

            {/* Legend */}
            <div className="flex items-center gap-5 mt-4 pt-4" style={{ borderTop: '1px solid #e8d5b7' }}>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-sm" style={{ background: '#3b2010' }}></div>
                <span className="text-[10px]" style={{ color: '#7a4f2a' }}>Actual</span>
              </div>
              <div className="flex items-center gap-2">
                <div
                  className="w-3 h-3 rounded-sm"
                  style={{
                    background: 'repeating-linear-gradient(45deg, #c9a96e, #c9a96e 2px, #e8d5b7 2px, #e8d5b7 5px)',
                  }}
                ></div>
                <span className="text-[10px]" style={{ color: '#7a4f2a' }}>Projected</span>
              </div>
              <span className="text-[10px] ml-auto" style={{ color: '#7a4f2a' }}>Source: IBEF, Statista 2024</span>
            </div>
          </div>

          {/* Right: Category Breakdown */}
          <div
            className="rounded-2xl p-7 md:p-8"
            style={{ background: '#faf7f2', border: '1px solid #e8d5b7' }}
          >
            <div className="mb-6">
              <p className="text-xs font-bold uppercase tracking-widest mb-1" style={{ color: '#c9a96e' }}>Faasley's Categories</p>
              <h3
                className="font-bold text-xl"
                style={{ fontFamily: "'Playfair Display', serif", color: '#2c1a0e' }}
              >
                Segment Breakdown
              </h3>
              <p className="text-xs mt-1" style={{ color: '#7a4f2a' }}>Market size of each category Faasley operates in</p>
            </div>

            <div className="space-y-5">
              {categorySegments.map((seg, i) => (
                <div key={seg.label}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs font-semibold" style={{ color: '#2c1a0e' }}>{seg.label}</span>
                    <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                      <span
                        className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                        style={{ background: '#f5ede0', color: '#c9a96e' }}
                      >
                        {seg.growth}
                      </span>
                      <span className="text-xs font-bold" style={{ color: '#2c1a0e' }}>{seg.size}</span>
                    </div>
                  </div>
                  <div className="h-2 rounded-full overflow-hidden" style={{ background: '#e8d5b7' }}>
                    <div
                      className="h-full rounded-full transition-all duration-700"
                      style={{
                        width: barsVisible ? `${seg.barWidth}%` : '0%',
                        transitionDelay: `${i * 120}ms`,
                        background: `linear-gradient(to right, #3b2010, #c9a96e)`,
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>

            {/* Total SAM callout */}
            <div
              className="mt-7 rounded-xl p-5 flex items-center gap-4"
              style={{ background: '#3b2010' }}
            >
              <div className="w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0" style={{ background: 'rgba(201,169,110,0.2)' }}>
                <i className="ri-focus-3-line text-lg" style={{ color: '#c9a96e' }}></i>
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-wider mb-0.5" style={{ color: '#c9a96e' }}>Faasley's SAM</p>
                <p className="font-bold text-xl" style={{ fontFamily: "'Playfair Display', serif", color: '#faf7f2' }}>
                  ₹1,00,700 Cr+
                </p>
                <p className="text-[10px]" style={{ color: 'rgba(232,213,183,0.7)' }}>Combined serviceable addressable market across all 5 categories</p>
              </div>
            </div>
          </div>
        </div>

        {/* ── Market Tailwinds ── */}
        <div
          className="rounded-2xl p-8 md:p-10"
          style={{ background: '#3b2010', border: '1px solid rgba(201,169,110,0.3)' }}
        >
          <div className="flex items-center gap-3 mb-8">
            <div className="w-9 h-9 flex items-center justify-center rounded-xl" style={{ background: 'rgba(201,169,110,0.2)' }}>
              <i className="ri-rocket-line text-base" style={{ color: '#c9a96e' }}></i>
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-widest" style={{ color: '#c9a96e' }}>Why Now?</p>
              <h3 className="font-bold text-lg" style={{ fontFamily: "'Playfair Display', serif", color: '#faf7f2' }}>
                4 Powerful Market Tailwinds
              </h3>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {tailwinds.map((tw, i) => (
              <div
                key={tw.title}
                className="rounded-xl p-5 transition-all duration-500"
                style={{
                  background: 'rgba(250,247,242,0.06)',
                  border: '1px solid rgba(201,169,110,0.2)',
                  transform: visible ? 'translateY(0)' : 'translateY(16px)',
                  opacity: visible ? 1 : 0,
                  transitionDelay: `${400 + i * 100}ms`,
                }}
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-xl mb-4" style={{ background: 'rgba(201,169,110,0.15)' }}>
                  <i className={`${tw.icon} text-base`} style={{ color: '#c9a96e' }}></i>
                </div>
                <h4 className="font-bold text-sm mb-2" style={{ fontFamily: "'Playfair Display', serif", color: '#faf7f2' }}>
                  {tw.title}
                </h4>
                <p className="text-xs leading-relaxed" style={{ color: 'rgba(232,213,183,0.75)' }}>{tw.desc}</p>
              </div>
            ))}
          </div>

          {/* Bottom quote */}
          <div
            className="mt-8 pt-7 flex flex-col md:flex-row items-start md:items-center gap-4"
            style={{ borderTop: '1px solid rgba(201,169,110,0.2)' }}
          >
            <i className="ri-double-quotes-l text-3xl flex-shrink-0" style={{ color: 'rgba(201,169,110,0.4)' }}></i>
            <p
              className="text-sm italic leading-relaxed"
              style={{ fontFamily: "'Playfair Display', serif", color: 'rgba(232,213,183,0.85)' }}
            >
              India's packaged food market is projected to reach <strong style={{ color: '#c9a96e' }}>₹9 Lakh Crore by 2030</strong> — making this one of the most compelling investment opportunities in the consumer sector today.
            </p>
          </div>
        </div>

      </div>
    </section>
  );
}

const investorFAQs = [
  {
    question: 'What is the exit strategy for investors?',
    answer: 'Investors have multiple exit options: (1) **Strategic Buyout** — Larger FMCG companies often acquire promising food brands; our multi-brand portfolio makes us an attractive acquisition target. (2) **Secondary Sale** — Sell your stake to new investors in future funding rounds at a higher valuation. (3) **Buyback** — Faasley can buy back shares after achieving profitability, typically Years 3–5. (4) **Dividend Income** — Continue holding for ongoing profit share without exiting. We project a 3–5 year horizon for meaningful exit opportunities.',
    icon: 'ri-door-open-line',
  },
  {
    question: 'How and when will profits be distributed?',
    answer: 'Profits are distributed **proportionally to equity holding** on an annual basis after: (1) Setting aside 30% for business reinvestment and growth, (2) Maintaining 3 months of operating expenses as reserves, (3) Auditor sign-off on annual financials. First distributions are projected for **FY 2026–27** once we achieve operational profitability. All distributions require board approval with full transparency on financials. Investors receive detailed P&L statements quarterly.',
    icon: 'ri-coins-line',
  },
  {
    question: 'What happens if sales targets are not met?',
    answer: 'We have a **contingency plan** for underperformance: (1) Pivot marketing strategy based on data — double down on channels showing traction. (2) Reduce burn rate by optimising packaging and logistics costs. (3) Explore B2B opportunities (hotels, corporates, airlines) for bulk orders. (4) Seek bridge funding if needed — our existing investor gets first right of refusal. (5) Worst case: orderly wind-down with asset liquidation — investors retain proportional claim on inventory, equipment, and brand IP. Founder stake ensures aligned incentives.',
    icon: 'ri-alert-line',
  },
  {
    question: 'What rights and protections do investors have?',
    answer: 'Investor protections include: (1) **Board Observer Seat** — Attend quarterly board meetings, vote on major decisions. (2) **Information Rights** — Monthly sales reports, quarterly financials, annual audits. (3) **Anti-Dilution Protection** — Preemptive rights in future funding rounds. (4) **Tag-Along Rights** — If founder sells shares, investors can sell proportionally. (5) **Veto Rights** — On major asset sales, new debt over ₹5L, or change in business model. (6) **Exit Consent** — Founder cannot force an exit without investor approval.',
    icon: 'ri-shield-star-line',
  },
  {
    question: 'How is my investment being used?',
    answer: 'Every rupee is tracked: **40%** — Raw materials & production (makhana, spices, tea, pickles, water sourcing). **25%** — Packaging & branding (premium pouches, labels, design). **20%** — Marketing & launch (digital ads, influencer partnerships, sampling). **15%** — Operations & logistics (storage, transport, initial team). We use a separate bank account for investor funds with monthly expense reports. Founder has committed personal funds as well, ensuring skin in the game.',
    icon: 'ri-funds-box-line',
  },
  {
    question: 'What makes Faasley different from competitors?',
    answer: 'Three key differentiators: (1) **Multi-Brand Strategy** — 6 distinct brands from Day 1 reduces single-product risk and captures multiple market segments simultaneously. (2) **Founder-Led Quality** — Kamini personally oversees every recipe, batch, and quality check — not outsourced to third parties. (3) **NRI Export Ready** — Products designed with export compliance in mind from inception, not as an afterthought. Our FSSAI certification, hygienic packaging, and authentic taste positioning create defensible moats.',
    icon: 'ri-vip-crown-line',
  },
  {
    question: 'What is the timeline to profitability?',
    answer: 'We project **break-even by Q4 2025** and **operational profitability by FY 2026–27**. Key milestones: Months 1–3: Production setup, initial inventory. Months 4–6: Regional launch, first 1,000 customers. Months 7–12: Scale to 5,000+ customers, expand to 2–3 cities. Year 2: Pan-India presence, B2B partnerships. Conservative projections assume ₹50L revenue in Year 1, ₹1.5Cr in Year 2. Monthly burn is controlled at ~₹70K post-launch.',
    icon: 'ri-time-line',
  },
  {
    question: 'Can I increase my investment later?',
    answer: 'Yes — existing investors have **right of first refusal** in future funding rounds. If we raise additional capital (e.g., Series A in Year 2–3), you can maintain or increase your ownership proportionally. Alternatively, you can invest more now up to ₹15L (we are flexible on amount). Early investors also get preferential terms in any future bridge rounds. We welcome investors who want to deepen their commitment as we grow.',
    icon: 'ri-add-circle-line',
  },
];

function InvestorFAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const sectionRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="py-20 md:py-28"
      style={{ background: 'linear-gradient(to bottom, #f5ede0, #faf7f2)', borderTop: '1px solid #e8d5b7' }}
    >
      <div className="w-full px-4 sm:px-6 lg:px-12 xl:px-20 max-w-5xl mx-auto">
        <SectionHeader
          icon="ri-question-answer-line"
          title="Investor FAQ"
          subtitle="Answers to the most common questions from potential partners"
        />

        <div className="space-y-4">
          {investorFAQs.map((faq, i) => {
            const isOpen = openIndex === i;
            return (
              <div
                key={i}
                className="rounded-xl overflow-hidden transition-all duration-300"
                style={{
                  background: isOpen ? '#faf7f2' : '#faf7f2',
                  border: '1px solid #e8d5b7',
                  transform: visible ? 'translateY(0)' : 'translateY(16px)',
                  opacity: visible ? 1 : 0,
                  transitionDelay: `${i * 80}ms`,
                }}
              >
                <button
                  onClick={() => setOpenIndex(isOpen ? null : i)}
                  className="w-full flex items-center gap-4 p-5 text-left cursor-pointer"
                >
                  <div
                    className="w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0 transition-all"
                    style={{
                      background: isOpen ? '#3b2010' : '#f5ede0',
                      border: '1px solid #e8d5b7',
                    }}
                  >
                    <i
                      className={`${faq.icon} text-base`}
                      style={{ color: isOpen ? '#c9a96e' : '#c9a96e' }}
                    ></i>
                  </div>
                  <span
                    className="flex-1 font-semibold text-sm"
                    style={{ fontFamily: "'Playfair Display', serif", color: '#2c1a0e' }}
                  >
                    {faq.question}
                  </span>
                  <div
                    className="w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0 transition-all"
                    style={{ background: isOpen ? '#c9a96e' : '#f5ede0' }}
                  >
                    <i
                      className={`${isOpen ? 'ri-subtract-line' : 'ri-add-line'} text-sm`}
                      style={{ color: isOpen ? '#2c1a0e' : '#7a4f2a' }}
                    ></i>
                  </div>
                </button>

                <div
                  className="overflow-hidden transition-all duration-300"
                  style={{
                    maxHeight: isOpen ? '500px' : '0',
                    opacity: isOpen ? 1 : 0,
                  }}
                >
                  <div className="px-5 pb-5 pl-[76px]">
                    <p
                      className="text-sm leading-relaxed"
                      style={{ color: '#5c3d1e' }}
                      dangerouslySetInnerHTML={{
                        __html: faq.answer.replace(/\*\*(.*?)\*\*/g, '<strong style="color:#2c1a0e">$1</strong>'),
                      }}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Still have questions CTA */}
        <div
          className="mt-10 rounded-2xl p-8 text-center"
          style={{
            background: '#3b2010',
            border: '1px solid rgba(201,169,110,0.3)',
            transform: visible ? 'translateY(0)' : 'translateY(20px)',
            opacity: visible ? 1 : 0,
            transition: 'all 0.5s ease 0.4s',
          }}
        >
          <h4
            className="font-bold text-xl mb-2"
            style={{ fontFamily: "'Playfair Display', serif", color: '#faf7f2' }}
          >
            Still have questions?
          </h4>
          <p className="text-sm mb-6" style={{ color: 'rgba(232,213,183,0.8)' }}>
            Schedule a one-on-one call with Kamini to discuss your specific concerns.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href="https://wa.me/918800889959?text=Hi%20Kamini%2C%20I%20have%20some%20questions%20about%20investing%20in%20Faasley%20Foods.%20Can%20we%20schedule%20a%20call%3F"
              target="_blank"
              rel="nofollow noreferrer"
              className="inline-flex items-center justify-center gap-2 font-semibold px-6 py-3 rounded-md text-sm transition-all whitespace-nowrap cursor-pointer"
              style={{ background: '#25D366', color: '#fff' }}
            >
              <i className="ri-whatsapp-line"></i>
              WhatsApp Your Questions
            </a>
            <a
              href="mailto:faasley@gmail.com?subject=Investment%20Questions%20-%20Faasley%20Foods"
              className="inline-flex items-center justify-center gap-2 font-semibold px-6 py-3 rounded-md text-sm transition-all whitespace-nowrap cursor-pointer"
              style={{ background: 'rgba(250,247,242,0.1)', color: '#faf7f2', border: '1px solid rgba(201,169,110,0.4)' }}
            >
              <i className="ri-mail-line"></i>
              Email Your Questions
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function InvestorPitchForm() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    investmentRange: '',
    background: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const body = new URLSearchParams();
      Object.entries(formData).forEach(([key, val]) => body.append(key, val));
      const res = await fetch('https://readdy.ai/api/form/d7al6b2bdfubkqslaoog', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        setSubmitted(true);
      } else {
        setError('Something went wrong. Please try again or WhatsApp us directly.');
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      className="py-20 md:py-28"
      style={{ background: 'linear-gradient(to bottom, #f5ede0, #faf7f2)', borderTop: '1px solid #e8d5b7' }}
    >
      <div className="w-full px-4 sm:px-6 lg:px-12 xl:px-20">
        <SectionHeader
          icon="ri-mail-send-line"
          title="Pitch to Us"
          subtitle="Interested in partnering with Faasley? Fill in your details and we'll get back to you within 48 hours"
        />

        <div className="grid lg:grid-cols-5 gap-10 items-start max-w-6xl mx-auto">

          {/* Left: Info panel */}
          <div className="lg:col-span-2 space-y-5">
            <div
              className="rounded-2xl p-7"
              style={{ background: '#3b2010', border: '1px solid rgba(201,169,110,0.3)' }}
            >
              <div className="w-11 h-11 flex items-center justify-center rounded-xl mb-5" style={{ background: 'rgba(201,169,110,0.2)' }}>
                <i className="ri-shake-hands-line text-xl" style={{ color: '#c9a96e' }}></i>
              </div>
              <h3
                className="font-bold text-xl mb-3"
                style={{ fontFamily: "'Playfair Display', serif", color: '#faf7f2' }}
              >
                What We're Looking For
              </h3>
              <p className="text-sm leading-relaxed mb-6" style={{ color: 'rgba(232,213,183,0.8)' }}>
                We're seeking a strategic partner — not just capital. Someone who brings expertise, network, and a shared vision for building India's next great food brand.
              </p>
              <div className="space-y-3">
                {[
                  { icon: 'ri-money-rupee-circle-line', text: '₹8–9 Lakhs investment' },
                  { icon: 'ri-pie-chart-2-line', text: '10–12% equity stake' },
                  { icon: 'ri-megaphone-line', text: 'Marketing & distribution expertise' },
                  { icon: 'ri-global-line', text: 'Pan-India or export network' },
                  { icon: 'ri-shield-check-line', text: 'FSSAI compliant, audit-ready' },
                ].map((item) => (
                  <div key={item.text} className="flex items-center gap-3">
                    <div className="w-7 h-7 flex items-center justify-center rounded-lg flex-shrink-0" style={{ background: 'rgba(201,169,110,0.15)' }}>
                      <i className={`${item.icon} text-xs`} style={{ color: '#c9a96e' }}></i>
                    </div>
                    <span className="text-sm" style={{ color: 'rgba(232,213,183,0.85)' }}>{item.text}</span>
                  </div>
                ))}
              </div>
            </div>

            <div
              className="rounded-2xl p-6"
              style={{ background: '#faf7f2', border: '1px solid #e8d5b7' }}
            >
              <p className="text-xs font-bold uppercase tracking-widest mb-4" style={{ color: '#c9a96e' }}>Prefer to talk directly?</p>
              <a
                href="https://wa.me/918800889959?text=Hi%20Kamini%2C%20I%20am%20interested%20in%20investing%20in%20Faasley%20Foods.%20Can%20we%20connect%3F"
                target="_blank"
                rel="nofollow noreferrer"
                className="flex items-center gap-3 rounded-xl px-5 py-4 transition-all cursor-pointer"
                style={{ background: '#25D366', color: '#fff' }}
              >
                <div className="w-9 h-9 flex items-center justify-center rounded-lg flex-shrink-0" style={{ background: 'rgba(255,255,255,0.2)' }}>
                  <i className="ri-whatsapp-line text-lg"></i>
                </div>
                <div>
                  <p className="font-bold text-sm">WhatsApp Kamini</p>
                  <p className="text-xs opacity-80">+91 8800889959</p>
                </div>
                <i className="ri-arrow-right-line ml-auto"></i>
              </a>
            </div>
          </div>

          {/* Right: Form */}
          <div className="lg:col-span-3">
            <div
              className="rounded-2xl p-8 md:p-10"
              style={{ background: '#faf7f2', border: '1px solid #e8d5b7' }}
            >
              {submitted ? (
                <div className="text-center py-12">
                  <div
                    className="w-16 h-16 flex items-center justify-center rounded-full mx-auto mb-5"
                    style={{ background: '#f5ede0', border: '2px solid #c9a96e' }}
                  >
                    <i className="ri-check-line text-2xl" style={{ color: '#c9a96e' }}></i>
                  </div>
                  <h3
                    className="font-bold text-2xl mb-3"
                    style={{ fontFamily: "'Playfair Display', serif", color: '#2c1a0e' }}
                  >
                    Pitch Received!
                  </h3>
                  <p className="text-sm leading-relaxed max-w-sm mx-auto" style={{ color: '#7a4f2a' }}>
                    Thank you for your interest in Faasley Foods. Kamini will review your pitch and get back to you within 48 hours.
                  </p>
                  <div className="flex items-center justify-center gap-2 mt-6">
                    <div className="h-px w-16" style={{ background: '#e8d5b7' }}></div>
                    <i className="ri-flower-line text-xs" style={{ color: '#c9a96e' }}></i>
                    <div className="h-px w-16" style={{ background: '#e8d5b7' }}></div>
                  </div>
                </div>
              ) : (
                <form
                  data-readdy-form
                  id="investor-pitch-form"
                  onSubmit={handleSubmit}
                  className="space-y-5"
                >
                  <div>
                    <p
                      className="font-bold text-xl mb-1"
                      style={{ fontFamily: "'Playfair Display', serif", color: '#2c1a0e' }}
                    >
                      Send Your Pitch
                    </p>
                    <p className="text-xs" style={{ color: '#7a4f2a' }}>All fields marked * are required</p>
                  </div>

                  {/* Row 1: Name + Email */}
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold mb-1.5" style={{ color: '#5c3d1e' }}>
                        Full Name *
                      </label>
                      <input
                        type="text"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Your full name"
                        className="w-full rounded-md px-4 py-3 text-sm outline-none transition-all"
                        style={{
                          background: '#f5ede0',
                          border: '1px solid #e8d5b7',
                          color: '#2c1a0e',
                        }}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold mb-1.5" style={{ color: '#5c3d1e' }}>
                        Email Address *
                      </label>
                      <input
                        type="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="your@email.com"
                        className="w-full rounded-md px-4 py-3 text-sm outline-none transition-all"
                        style={{
                          background: '#f5ede0',
                          border: '1px solid #e8d5b7',
                          color: '#2c1a0e',
                        }}
                      />
                    </div>
                  </div>

                  {/* Row 2: Phone + Company */}
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold mb-1.5" style={{ color: '#5c3d1e' }}>
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        required
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+91 XXXXX XXXXX"
                        className="w-full rounded-md px-4 py-3 text-sm outline-none transition-all"
                        style={{
                          background: '#f5ede0',
                          border: '1px solid #e8d5b7',
                          color: '#2c1a0e',
                        }}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold mb-1.5" style={{ color: '#5c3d1e' }}>
                        Company / Organisation
                      </label>
                      <input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        placeholder="Your company name"
                        className="w-full rounded-md px-4 py-3 text-sm outline-none transition-all"
                        style={{
                          background: '#f5ede0',
                          border: '1px solid #e8d5b7',
                          color: '#2c1a0e',
                        }}
                      />
                    </div>
                  </div>

                  {/* Row 3: Investment Range */}
                  <div>
                    <label className="block text-xs font-semibold mb-1.5" style={{ color: '#5c3d1e' }}>
                      Investment Range *
                    </label>
                    <select
                      name="investmentRange"
                      required
                      value={formData.investmentRange}
                      onChange={handleChange}
                      className="w-full rounded-md px-4 py-3 text-sm outline-none transition-all cursor-pointer"
                      style={{
                        background: '#f5ede0',
                        border: '1px solid #e8d5b7',
                        color: formData.investmentRange ? '#2c1a0e' : '#7a4f2a',
                      }}
                    >
                      <option value="" disabled>Select investment range</option>
                      <option value="₹5–8 Lakhs">₹5–8 Lakhs</option>
                      <option value="₹8–9 Lakhs (Target)">₹8–9 Lakhs (Target)</option>
                      <option value="₹9–15 Lakhs">₹9–15 Lakhs</option>
                      <option value="₹15 Lakhs+">₹15 Lakhs+</option>
                      <option value="Open to Discussion">Open to Discussion</option>
                    </select>
                  </div>

                  {/* Row 4: Background */}
                  <div>
                    <label className="block text-xs font-semibold mb-1.5" style={{ color: '#5c3d1e' }}>
                      Your Background / Expertise *
                    </label>
                    <select
                      name="background"
                      required
                      value={formData.background}
                      onChange={handleChange}
                      className="w-full rounded-md px-4 py-3 text-sm outline-none transition-all cursor-pointer"
                      style={{
                        background: '#f5ede0',
                        border: '1px solid #e8d5b7',
                        color: formData.background ? '#2c1a0e' : '#7a4f2a',
                      }}
                    >
                      <option value="" disabled>Select your background</option>
                      <option value="FMCG / Food Industry">FMCG / Food Industry</option>
                      <option value="Marketing & Branding">Marketing &amp; Branding</option>
                      <option value="Distribution & Retail">Distribution &amp; Retail</option>
                      <option value="Angel Investor / VC">Angel Investor / VC</option>
                      <option value="E-commerce / D2C">E-commerce / D2C</option>
                      <option value="Export / International Trade">Export / International Trade</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  {/* Row 5: Message */}
                  <div>
                    <label className="block text-xs font-semibold mb-1.5" style={{ color: '#5c3d1e' }}>
                      Your Message / Pitch *
                    </label>
                    <textarea
                      name="message"
                      required
                      value={formData.message}
                      onChange={handleChange}
                      rows={4}
                      maxLength={500}
                      placeholder="Tell us about yourself, your network, and how you can help Faasley grow..."
                      className="w-full rounded-md px-4 py-3 text-sm outline-none transition-all resize-none"
                      style={{
                        background: '#f5ede0',
                        border: '1px solid #e8d5b7',
                        color: '#2c1a0e',
                      }}
                    />
                    <p className="text-[10px] text-right mt-1" style={{ color: '#7a4f2a' }}>
                      {formData.message.length}/500
                    </p>
                  </div>

                  {error && (
                    <p className="text-xs rounded-md px-4 py-3" style={{ background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca' }}>
                      {error}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full font-bold py-4 rounded-md flex items-center justify-center gap-2 transition-all whitespace-nowrap text-sm cursor-pointer"
                    style={{
                      background: submitting ? '#7a4f2a' : '#3b2010',
                      color: '#faf7f2',
                      opacity: submitting ? 0.8 : 1,
                    }}
                  >
                    {submitting ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i>
                        Sending Pitch...
                      </>
                    ) : (
                      <>
                        <i className="ri-send-plane-line"></i>
                        Submit My Pitch
                      </>
                    )}
                  </button>

                  <p className="text-[10px] text-center" style={{ color: '#7a4f2a' }}>
                    By submitting, you agree to our{' '}
                    <a href="/privacy" className="underline" style={{ color: '#c9a96e' }}>Privacy Policy</a>.
                    We respond within 48 hours.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function InvestorsPage() {
  useEffect(() => {
    const schemaData = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'WebPage',
          '@id': `${SITE_URL}/investors#webpage`,
          url: `${SITE_URL}/investors`,
          name: 'Investor Pitch | Faasley Foods — Invest in Premium Indian Food Brand',
          description: 'Invest in Faasley Foods — India\'s next premium packaged food brand. Seeking ₹8–9 Lakhs for 10–12% equity. FSSAI certified, 9+ products, 5+ categories.',
          breadcrumb: {
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
              { '@type': 'ListItem', position: 2, name: 'Investors', item: `${SITE_URL}/investors` },
            ],
          },
        },
        {
          '@type': 'Corporation',
          name: 'Faasleyfoods Pvt. Ltd.',
          legalName: 'Faasleyfoods Pvt. Ltd.',
          url: SITE_URL,
          foundingDate: '2024',
          founder: {
            '@type': 'Person',
            name: 'Kamini Thakur',
            jobTitle: 'Founder & CEO',
          },
          description: 'India\'s next premium packaged food brand — authentic taste, hygienic packaging, and integrity in every product. Seeking strategic investment partner.',
          numberOfEmployees: { '@type': 'QuantitativeValue', minValue: 1, maxValue: 10 },
          address: {
            '@type': 'PostalAddress',
            addressCountry: 'IN',
          },
        },
      ],
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-investors';
    script.textContent = JSON.stringify(schemaData);
    document.head.appendChild(script);
    document.title = 'Investor Pitch | Faasley Foods — Invest in Premium Indian Food Brand';
    return () => {
      const el = document.getElementById('schema-investors');
      if (el) el.remove();
    };
  }, []);

  return (
    <div className="min-h-screen" style={{ background: '#faf7f2' }}>
      <Navbar />

      {/* ── HERO ── */}
      <div
        className="pt-[200px] md:pt-[220px] pb-16 md:pb-24"
        style={{ background: 'linear-gradient(to bottom, #f5ede0, #faf7f2)' }}
      >
        <div className="w-full px-4 sm:px-6 lg:px-12 xl:px-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left: Text */}
            <div>
              {/* Decorative label */}
              <div className="flex items-center gap-3 mb-5">
                <div className="h-px w-8" style={{ background: '#c9a96e' }}></div>
                <span
                  className="text-xs font-bold uppercase tracking-[0.2em]"
                  style={{ color: '#c9a96e' }}
                >
                  Investor Pitch Deck · 2024–25
                </span>
              </div>

              <h1
                className="font-bold leading-tight mb-5"
                style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(2.2rem, 5vw, 3.5rem)', color: '#2c1a0e' }}
              >
                Invest in<br />
                <span style={{ color: '#c9a96e' }}>Faasley Foods</span>
              </h1>

              <p className="text-base leading-relaxed mb-8 max-w-xl" style={{ color: '#5c3d1e' }}>
                India's next premium packaged food brand — authentic taste, hygienic packaging, and integrity in every product.
              </p>

              <div className="flex flex-col sm:flex-row gap-3 mb-10">
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-2 font-semibold px-8 py-3.5 rounded-md text-sm transition-all whitespace-nowrap"
                  style={{ background: '#3b2010', color: '#faf7f2' }}
                >
                  <i className="ri-calendar-check-line"></i>
                  Schedule a Meeting
                </Link>
                <DownloadPitchDeckButton variant="dark" />
                <a
                  href="https://wa.me/918800889959"
                  target="_blank"
                  rel="nofollow noreferrer"
                  className="inline-flex items-center justify-center gap-2 font-medium px-8 py-3.5 rounded-md text-sm transition-all whitespace-nowrap"
                  style={{ border: '1px solid #c9a96e', color: '#3b2010', background: 'transparent' }}
                >
                  <i className="ri-whatsapp-line"></i>
                  WhatsApp Us
                </a>
              </div>

              {/* Inline stats */}
              <div
                className="flex flex-wrap gap-6 pt-8"
                style={{ borderTop: '1px solid #e8d5b7' }}
              >
                {stats.map((s, i) => (
                  <div key={s.label} className="flex items-center gap-4">
                    {i > 0 && <div className="w-px h-8 hidden sm:block" style={{ background: '#e8d5b7' }}></div>}
                    <div>
                      <p
                        className="font-bold text-2xl"
                        style={{ fontFamily: "'Playfair Display', serif", color: '#c9a96e' }}
                      >
                        {s.value}
                      </p>
                      <p className="text-xs mt-0.5" style={{ color: '#7a4f2a' }}>{s.label}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: Founder image */}
            <div className="relative flex justify-center lg:justify-end">
              <div className="relative w-72 md:w-80 lg:w-96">
                <div
                  className="absolute inset-0 rounded-2xl translate-x-3 translate-y-3"
                  style={{ background: '#e8d5b7' }}
                ></div>
                <div
                  className="relative rounded-2xl overflow-hidden"
                  style={{ aspectRatio: '3/4', border: '1px solid #e8d5b7' }}
                >
                  <img
                    src="https://storage.readdy-site.link/project_files/c44a33b9-73a7-4f29-8c86-80a04dae53c1/5cb346ab-a7f1-4cf6-953f-cf38d742223f_Kamini.jpg?v=7ff1f630b615292716d05c68d8042a2b"
                    alt="Kamini Thakur, Founder & CEO"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div
                  className="absolute bottom-5 left-5 right-5 rounded-xl px-5 py-4"
                  style={{ background: 'rgba(250,247,242,0.97)', border: '1px solid #e8d5b7' }}
                >
                  <p className="text-xs font-bold uppercase tracking-widest mb-0.5" style={{ color: '#c9a96e' }}>Founder &amp; CEO</p>
                  <p
                    className="font-bold text-base"
                    style={{ fontFamily: "'Playfair Display', serif", color: '#2c1a0e' }}
                  >
                    Kamini Thakur
                  </p>
                  <p className="text-xs mt-0.5" style={{ color: '#7a4f2a' }}>Faasleyfoods Pvt. Ltd.</p>
                </div>
                <div
                  className="absolute -top-2 -right-2 rounded-xl px-4 py-3 text-center"
                  style={{ background: '#3b2010' }}
                >
                  <p className="font-bold text-xl leading-none" style={{ color: '#c9a96e' }}>10–12%</p>
                  <p className="text-xs mt-0.5" style={{ color: '#e8d5b7' }}>Equity</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="mt-16 flex items-center justify-center gap-2">
          <div className="h-px flex-1 max-w-xs" style={{ background: '#e8d5b7' }}></div>
          <i className="ri-flower-line text-sm" style={{ color: '#c9a96e' }}></i>
          <div className="h-px flex-1 max-w-xs" style={{ background: '#e8d5b7' }}></div>
        </div>
      </div>

      {/* ── EXECUTIVE SUMMARY ── */}
      <section
        className="py-20 md:py-28"
        style={{ background: 'linear-gradient(to bottom, #faf7f2, #f5ede0)' }}
      >
        <div className="w-full px-4 sm:px-6 lg:px-12 xl:px-20">
          <SectionHeader icon="ri-file-text-line" title="Executive Summary" subtitle="A clear opportunity in India's booming packaged food market" />

          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <div>
              <p className="text-base leading-relaxed mb-5" style={{ color: '#5c3d1e' }}>
                Faasleyfoods Pvt. Ltd. is building a portfolio of premium Indian packaged food brands — starting with 9 distinct products across 5 categories. We combine authentic homemade recipes with modern hygienic packaging to serve health-conscious Indian consumers.
              </p>
              <p className="text-base leading-relaxed mb-8" style={{ color: '#5c3d1e' }}>
                We are seeking a <strong style={{ color: '#2c1a0e' }}>strategic partner</strong> who brings marketing expertise, distribution network, and business scaling guidance — in exchange for <strong style={{ color: '#2c1a0e' }}>10–12% equity</strong> for an investment of <strong style={{ color: '#2c1a0e' }}>₹8–9 Lakhs</strong>.
              </p>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { label: 'Investment Ask', value: '₹8–9 Lakhs', sub: 'One-time seed capital' },
                  { label: 'Equity Offered', value: '10–12%', sub: 'Strategic partner stake' },
                  { label: 'Founder Stake', value: '88–90%', sub: 'Controlling interest' },
                  { label: 'Profit Share', value: 'Equity Based', sub: 'Proportional returns' },
                ].map((item) => (
                  <div
                    key={item.label}
                    className="rounded-xl p-5"
                    style={{ background: '#faf7f2', border: '1px solid #e8d5b7' }}
                  >
                    <p className="text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: '#c9a96e' }}>{item.label}</p>
                    <p
                      className="font-bold text-xl mb-0.5"
                      style={{ fontFamily: "'Playfair Display', serif", color: '#2c1a0e' }}
                    >
                      {item.value}
                    </p>
                    <p className="text-xs" style={{ color: '#7a4f2a' }}>{item.sub}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Founder Card */}
            <div className="rounded-2xl overflow-hidden" style={{ border: '1px solid #e8d5b7' }}>
              <div className="relative overflow-hidden" style={{ height: '320px' }}>
                <img
                  src="https://storage.readdy-site.link/project_files/c44a33b9-73a7-4f29-8c86-80a04dae53c1/5cb346ab-a7f1-4cf6-953f-cf38d742223f_Kamini.jpg?v=7ff1f630b615292716d05c68d8042a2b"
                  alt="Kamini Thakur - Founder"
                  className="w-full h-full object-cover object-top"
                />
                <div
                  className="absolute inset-0"
                  style={{ background: 'linear-gradient(to top, rgba(44,26,14,0.65) 0%, transparent 55%)' }}
                />
                <div className="absolute bottom-4 left-6">
                  <span
                    className="text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider"
                    style={{ background: '#c9a96e', color: '#2c1a0e' }}
                  >
                    Founder &amp; Visionary
                  </span>
                </div>
              </div>
              <div className="p-7" style={{ background: '#faf7f2' }}>
                <h3
                  className="font-bold text-2xl mb-1"
                  style={{ fontFamily: "'Playfair Display', serif", color: '#2c1a0e' }}
                >
                  Kamini Thakur
                </h3>
                <p className="text-sm mb-4" style={{ color: '#c9a96e' }}>Faasleyfoods Pvt. Ltd.</p>
                <blockquote
                  className="text-sm italic leading-relaxed pl-4 mb-5"
                  style={{ color: '#5c3d1e', borderLeft: '2px solid #c9a96e', fontFamily: "'Playfair Display', serif" }}
                >
                  "Faasley एक premium food brand होगा जो hygienic, homemade style और authentic taste वाले products बनाएगा।"
                </blockquote>
                <div className="flex items-center gap-2 mb-5">
                  <div className="h-px flex-1" style={{ background: '#e8d5b7' }}></div>
                  <i className="ri-flower-line text-xs" style={{ color: '#c9a96e' }}></i>
                  <div className="h-px flex-1" style={{ background: '#e8d5b7' }}></div>
                </div>
                <div className="grid grid-cols-2 gap-2.5">
                  {['Product Development', 'Manufacturing Setup', 'Quality Control', 'Creative Branding'].map((skill) => (
                    <div key={skill} className="flex items-center gap-2 text-xs" style={{ color: '#5c3d1e' }}>
                      <i className="ri-check-line text-sm" style={{ color: '#c9a96e' }}></i>
                      {skill}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── TEAM / FOUNDER ── */}
      <FounderSection />

      {/* ── MARKET SIZE / TAM ── */}
      <MarketSizeSection />

      {/* ── USE OF FUNDS ── */}
      <section
        className="py-20 md:py-28"
        style={{ background: 'linear-gradient(to bottom, #f5ede0, #faf7f2)' }}
      >
        <div className="w-full px-4 sm:px-6 lg:px-12 xl:px-20">
          <SectionHeader icon="ri-funds-line" title="Use of Funds" subtitle="Every rupee invested is allocated with purpose and accountability" />

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              {useOfFunds.map((item, idx) => {
                const barColors = ['#c9a96e', '#3b2010', '#e8d5b7', '#7a4f2a'];
                return (
                  <div key={item.label}>
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-medium text-sm" style={{ color: '#2c1a0e' }}>{item.label}</span>
                      <div className="flex items-center gap-3">
                        <span className="text-xs" style={{ color: '#7a4f2a' }}>{item.amount}</span>
                        <span className="font-bold text-sm w-10 text-right" style={{ color: '#2c1a0e' }}>{item.percent}%</span>
                      </div>
                    </div>
                    <div className="h-2.5 rounded-full overflow-hidden" style={{ background: '#e8d5b7' }}>
                      <div
                        className="h-full rounded-full transition-all duration-700"
                        style={{ width: `${item.percent}%`, background: barColors[idx] }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="rounded-2xl p-8 md:p-10" style={{ background: '#faf7f2', border: '1px solid #e8d5b7' }}>
              <h3
                className="font-bold text-2xl mb-2"
                style={{ fontFamily: "'Playfair Display', serif", color: '#2c1a0e' }}
              >
                Investment Returns
              </h3>
              <p className="text-sm mb-7" style={{ color: '#7a4f2a' }}>Illustrative projections based on equity percentage</p>
              <div className="space-y-4">
                {[
                  { scenario: 'Conservative (₹10L profit)', investor: '₹1–1.2L', founder: '₹8.8–9L' },
                  { scenario: 'Moderate (₹25L profit)', investor: '₹2.5–3L', founder: '₹22–22.5L' },
                  { scenario: 'Optimistic (₹50L profit)', investor: '₹5–6L', founder: '₹44–45L' },
                ].map((row) => (
                  <div
                    key={row.scenario}
                    className="rounded-xl p-5"
                    style={{ background: '#f5ede0', border: '1px solid #e8d5b7' }}
                  >
                    <p className="text-xs uppercase tracking-wider mb-3" style={{ color: '#7a4f2a' }}>{row.scenario}</p>
                    <div className="flex justify-between">
                      <div>
                        <p className="text-xs mb-0.5" style={{ color: '#7a4f2a' }}>Investor Return</p>
                        <p
                          className="font-bold text-lg"
                          style={{ fontFamily: "'Playfair Display', serif", color: '#c9a96e' }}
                        >
                          {row.investor}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs mb-0.5" style={{ color: '#7a4f2a' }}>Founder Return</p>
                        <p
                          className="font-bold text-lg"
                          style={{ fontFamily: "'Playfair Display', serif", color: '#2c1a0e' }}
                        >
                          {row.founder}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── OUR BRANDS ── */}
      <section
        className="py-20 md:py-28"
        style={{ background: 'linear-gradient(to bottom, #faf7f2, #f5ede0)' }}
      >
        <div className="w-full px-4 sm:px-6 lg:px-12 xl:px-20">
          <SectionHeader icon="ri-store-2-line" title="Our Brand Family" subtitle="9+ products across 6 distinct brands — diversified from day one" />

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {brands.map((brand) => (
              <div
                key={brand.name}
                className="rounded-xl p-6 transition-all cursor-default"
                style={{ background: '#faf7f2', border: '1px solid #e8d5b7' }}
              >
                <div className="flex items-start gap-4">
                  <div
                    className="w-12 h-12 flex items-center justify-center rounded-xl flex-shrink-0"
                    style={{ background: '#f5ede0', border: '1px solid #e8d5b7' }}
                  >
                    <i className={`${brand.icon} text-xl`} style={{ color: '#c9a96e' }}></i>
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wider mb-0.5" style={{ color: '#c9a96e' }}>{brand.category}</p>
                    <h3
                      className="font-bold text-lg leading-tight"
                      style={{ fontFamily: "'Playfair Display', serif", color: '#2c1a0e' }}
                    >
                      {brand.name}
                    </h3>
                    <p className="text-sm mt-1 leading-relaxed" style={{ color: '#7a4f2a' }}>{brand.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHY INVEST ── */}
      <section
        className="py-20 md:py-28"
        style={{ background: 'linear-gradient(to bottom, #f5ede0, #faf7f2)' }}
      >
        <div className="w-full px-4 sm:px-6 lg:px-12 xl:px-20">
          <SectionHeader icon="ri-question-line" title="Why Faasley?" subtitle="The case for investing in India's next premium food brand" />

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {whyInvest.map((item) => (
              <div
                key={item.title}
                className="rounded-xl p-7"
                style={{ background: '#faf7f2', border: '1px solid #e8d5b7' }}
              >
                <div
                  className="w-12 h-12 flex items-center justify-center rounded-xl mb-5"
                  style={{ background: '#f5ede0', border: '1px solid #e8d5b7' }}
                >
                  <i className={`${item.icon} text-xl`} style={{ color: '#c9a96e' }}></i>
                </div>
                <h3
                  className="font-bold text-lg mb-2"
                  style={{ fontFamily: "'Playfair Display', serif", color: '#2c1a0e' }}
                >
                  {item.title}
                </h3>
                <p className="text-sm leading-relaxed" style={{ color: '#7a4f2a' }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── GROWTH TIMELINE ── */}
      <section
        className="py-20 md:py-28"
        style={{ background: 'linear-gradient(to bottom, #faf7f2, #f5ede0)', borderTop: '1px solid #e8d5b7', borderBottom: '1px solid #e8d5b7' }}
      >
        <div className="w-full px-4 sm:px-6 lg:px-12 xl:px-20">
          <SectionHeader icon="ri-map-2-line" title="Growth Timeline" subtitle="A clear, phased path from launch to national and international scale" />

          <div className="relative">
            <div
              className="hidden lg:block absolute top-8 left-0 right-0 h-px mx-20"
              style={{ background: '#e8d5b7' }}
            />
            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6">
              {timeline.map((item, i) => (
                <div key={item.year} className="relative text-center">
                  <div
                    className="w-16 h-16 flex items-center justify-center rounded-full mx-auto mb-5 relative z-10"
                    style={{ background: '#3b2010', border: '4px solid #f5ede0' }}
                  >
                    <span className="font-bold text-sm" style={{ color: '#c9a96e' }}>{i + 1}</span>
                  </div>
                  <p className="text-xs font-bold uppercase tracking-wider mb-1" style={{ color: '#c9a96e' }}>{item.year}</p>
                  <h3
                    className="font-bold text-base mb-2"
                    style={{ fontFamily: "'Playfair Display', serif", color: '#2c1a0e' }}
                  >
                    {item.title}
                  </h3>
                  <p className="text-xs leading-relaxed" style={{ color: '#7a4f2a' }}>{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── PARTNER ROLE ── */}
      <section
        className="py-20 md:py-28"
        style={{ background: 'linear-gradient(to bottom, #f5ede0, #faf7f2)' }}
      >
        <div className="w-full px-4 sm:px-6 lg:px-12 xl:px-20">
          <SectionHeader icon="ri-links-line" title="Strategic Partner Role" subtitle="Beyond capital — we need expertise, network, and vision" />

          <div className="grid lg:grid-cols-2 gap-14 items-start">
            <div>
              <p className="text-base leading-relaxed mb-8" style={{ color: '#5c3d1e' }}>
                Beyond capital, we're looking for a partner who can actively contribute to Faasley's growth through expertise, network, and strategic guidance.
              </p>
              <div className="space-y-4">
                {[
                  { icon: 'ri-megaphone-line', title: 'Marketing Strategy', desc: 'Brand positioning, digital marketing, and consumer outreach campaigns' },
                  { icon: 'ri-truck-line', title: 'Distribution Network', desc: 'Retail partnerships, modern trade access, and e-commerce channel setup' },
                  { icon: 'ri-line-chart-line', title: 'Business Scaling', desc: 'Operational guidance, vendor negotiations, and growth planning' },
                  { icon: 'ri-links-line', title: 'Industry Connections', desc: 'Introductions to retailers, distributors, and potential B2B buyers' },
                ].map((item) => (
                  <div
                    key={item.title}
                    className="flex items-start gap-4 rounded-xl p-5"
                    style={{ background: '#faf7f2', border: '1px solid #e8d5b7' }}
                  >
                    <div
                      className="w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0"
                      style={{ background: '#f5ede0' }}
                    >
                      <i className={`${item.icon}`} style={{ color: '#c9a96e' }}></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-sm mb-0.5" style={{ color: '#2c1a0e' }}>{item.title}</h4>
                      <p className="text-xs leading-relaxed" style={{ color: '#7a4f2a' }}>{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Investment Summary */}
            <div className="rounded-2xl p-8 md:p-10" style={{ background: '#faf7f2', border: '1px solid #e8d5b7' }}>
              <h3
                className="font-bold text-2xl mb-2"
                style={{ fontFamily: "'Playfair Display', serif", color: '#2c1a0e' }}
              >
                Investment Summary
              </h3>
              <p className="text-sm mb-8" style={{ color: '#7a4f2a' }}>Everything you need to know at a glance</p>
              <div className="space-y-5">
                {[
                  { label: 'Company', value: 'Faasleyfoods Pvt. Ltd.' },
                  { label: 'Investment Required', value: '₹8,00,000 – ₹9,00,000' },
                  { label: 'Equity Offered', value: '10% – 12%' },
                  { label: 'Founder Stake', value: '88% – 90%' },
                  { label: 'Profit Sharing', value: 'Proportional to equity' },
                  { label: 'FSSAI Compliant', value: 'Yes — All Products' },
                  { label: 'Stage', value: 'Pre-Launch / Seed' },
                ].map((row) => (
                  <div
                    key={row.label}
                    className="flex justify-between items-center pb-4"
                    style={{ borderBottom: '1px solid #e8d5b7' }}
                  >
                    <span className="text-sm" style={{ color: '#7a4f2a' }}>{row.label}</span>
                    <span className="font-semibold text-sm text-right" style={{ color: '#2c1a0e' }}>{row.value}</span>
                  </div>
                ))}
              </div>
              <div className="mt-8 flex flex-col gap-3">
                <Link
                  to="/contact"
                  className="w-full font-bold py-4 rounded-md flex items-center justify-center gap-2 transition-all whitespace-nowrap text-sm"
                  style={{ background: '#3b2010', color: '#faf7f2' }}
                >
                  <i className="ri-calendar-check-line"></i>
                  Schedule a Meeting
                </Link>
                <div className="w-full flex justify-center">
                  <DownloadPitchDeckButton variant="outline" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── INVESTOR PITCH FORM ── */}
      <InvestorPitchForm />

      {/* ── INVESTOR FAQ ── */}
      <InvestorFAQSection />

      {/* ── CTA ── */}
      <section
        className="py-20 md:py-28"
        style={{ background: 'linear-gradient(to bottom, #faf7f2, #f5ede0)', borderTop: '1px solid #e8d5b7' }}
      >
        <div className="w-full px-4 sm:px-6 lg:px-12 xl:px-20 text-center">
          {/* Decorative header */}
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="h-px flex-1 max-w-[80px]" style={{ background: '#c9a96e' }}></div>
            <i className="ri-flower-line text-base" style={{ color: '#c9a96e' }}></i>
            <div className="h-px flex-1 max-w-[80px]" style={{ background: '#c9a96e' }}></div>
          </div>

          <h2
            className="font-bold mb-5"
            style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(1.8rem, 4vw, 3rem)', color: '#2c1a0e' }}
          >
            Ready to Partner with Faasley?
          </h2>
          <p className="text-base max-w-2xl mx-auto mb-10 leading-relaxed" style={{ color: '#5c3d1e' }}>
            "With your marketing expertise and strategic guidance, Faasley can grow into a powerful food brand that reaches every Indian household."
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 font-bold px-10 py-4 rounded-md transition-all whitespace-nowrap"
              style={{ background: '#3b2010', color: '#faf7f2' }}
            >
              <i className="ri-mail-send-line"></i>
              Get in Touch
            </Link>
            <DownloadPitchDeckButton variant="dark" />
            <a
              href="https://wa.me/918800889959"
              target="_blank"
              rel="nofollow noreferrer"
              className="inline-flex items-center justify-center gap-2 font-semibold px-10 py-4 rounded-md transition-all whitespace-nowrap"
              style={{ border: '1px solid #c9a96e', color: '#3b2010', background: 'transparent' }}
            >
              <i className="ri-whatsapp-line"></i>
              +91 8800889959
            </a>
          </div>

          {/* Bottom decorative divider */}
          <div className="flex items-center justify-center gap-2">
            <div className="h-px w-24" style={{ background: '#e8d5b7' }}></div>
            <div className="w-1.5 h-1.5 rounded-full" style={{ background: '#c9a96e' }}></div>
            <div className="h-px w-24" style={{ background: '#e8d5b7' }}></div>
          </div>
          <p
            className="mt-5 font-bold italic"
            style={{ fontFamily: "'Playfair Display', serif", color: '#7a4f2a', fontSize: '0.95rem' }}
          >
            Crafted with Love &amp; Care — Faasley Foods
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
